import {
  BLOCK_ORDER,
  BLOCKS,
  type BlockKind,
  FUSE_MAX_TRACKS,
  type FlowEvent,
  type Graph,
  type GraphNode,
  type GunKind,
  hasInput,
  outputsOf,
  type SensorKind,
  wireProblem,
} from "./graph";
import { FIXED_GUNS, SENSORS, WEAPONS } from "./world";

/** Where earlier versions saved the graph between games; cleared on load because every game now starts fresh. */
const LEGACY_STORAGE_KEY = "catalyst-game.ground-graph";
const GRAPH_W = 940;
const GRAPH_H = 420;
const NODE_W = 132;
const NODE_H = 74;
const DRAG_THRESHOLD_PX = 5;
const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * The ground map's pipeline editor: a node graph. Sensors placed on the map appear on the left,
 * the guns sit on the right, and the player drags processing blocks in between and draws wires from
 * an output port onto another node. Clicking a wire cuts it.
 */
export class GraphEditor {
  readonly graph: Graph;
  private area: HTMLElement;
  private svg: SVGSVGElement;
  private info: HTMLElement;
  private nodeEls = new Map<string, HTMLElement>();
  private wireEls = new Map<string, SVGPathElement>();
  private nextId = 1;

  constructor(root: HTMLElement) {
    root.innerHTML = `
      <div class="graph-toolbar">
        <span class="graph-label">Blocks</span>
        <div class="graph-palette"></div>
      </div>
      <div class="graph-scroll"><div class="graph" style="width:${GRAPH_W}px;height:${GRAPH_H}px"></div></div>
      <p class="graph-info">Place sensors on the map, then drag from a node's right-hand port onto another node to wire it. Click a wire to cut it.</p>`;
    this.area = root.querySelector(".graph")!;
    this.info = root.querySelector(".graph-info")!;
    this.svg = document.createElementNS(SVG_NS, "svg");
    this.svg.classList.add("graph-wires");
    this.svg.setAttribute("width", String(GRAPH_W));
    this.svg.setAttribute("height", String(GRAPH_H));
    this.area.append(this.svg);

    this.graph = { nodes: defaultGuns(), wires: [] };
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // Storage unavailable: nothing to clear.
    }

    const palette = root.querySelector<HTMLElement>(".graph-palette")!;
    for (const kind of BLOCK_ORDER) palette.append(this.paletteButton(kind));

    for (const n of this.graph.nodes) this.mountNode(n);
    this.renderWires();
  }

  // ---------------------------------------------------------------- sensors from the map

  addSensor(id: string, kind: SensorKind, label: string): void {
    const sensors = this.graph.nodes.filter((n) => n.type === "sensor").length;
    const col = Math.floor(sensors / 4);
    const node: GraphNode = {
      id,
      type: "sensor",
      sensor: kind,
      label,
      x: 10 + col * (NODE_W + 16),
      y: 10 + (sensors % 4) * (NODE_H + 10),
    };
    this.graph.nodes.push(node);
    this.mountNode(node);
    this.changed();
  }

  /** Back to a fresh graph for a new game: just the fixed guns, at their default positions, with no wires. */
  reset(): void {
    for (const el of this.nodeEls.values()) el.remove();
    this.nodeEls.clear();
    this.graph.nodes = defaultGuns();
    this.graph.wires = [];
    for (const n of this.graph.nodes) this.mountNode(n);
    this.changed();
  }

  /** A gun bought during the game. Bought guns fill the bottom row, right to left, then the row above. */
  addGun(id: string, kind: GunKind, label: string): void {
    const bought = this.graph.nodes.filter((n) => n.type === "gun" && !isFixedGun(n.id)).length;
    const perRow = 5;
    const node: GraphNode = {
      id,
      type: "gun",
      gun: kind,
      label,
      ...clampNode({
        x: GRAPH_W - NODE_W - 16 - ((bought % perRow) + 1) * (NODE_W + 14),
        y: GRAPH_H - NODE_H - 8 - Math.floor(bought / perRow) * (NODE_H + 10),
      }),
    };
    this.graph.nodes.push(node);
    this.mountNode(node);
    this.changed();
  }

  removeNode(id: string): void {
    this.graph.nodes = this.graph.nodes.filter((n) => n.id !== id);
    this.graph.wires = this.graph.wires.filter((w) => w.from !== id && w.to !== id);
    this.nodeEls.get(id)?.remove();
    this.nodeEls.delete(id);
    this.changed();
  }

  // ---------------------------------------------------------------- flow animation

  flash(ev: FlowEvent): void {
    if (ev.wireId) {
      const el = this.wireEls.get(ev.wireId);
      if (!el) return;
      el.classList.remove("hot");
      void el.getBoundingClientRect();
      el.classList.add("hot");
      return;
    }
    const el = ev.nodeId ? this.nodeEls.get(ev.nodeId) : undefined;
    if (!el) return;
    el.classList.remove("flash-pass", "flash-drop");
    void el.offsetWidth;
    el.classList.add(ev.passed ? "flash-pass" : "flash-drop");
  }

  /** Shows a block's load: the bar fills to capacity and turns red past it; a tripped block shows its cooldown. */
  setLoad(nodeId: string, load: number, tracks: number | null, cooldown: number): void {
    const node = this.nodeEls.get(nodeId);
    const el = node?.querySelector<HTMLElement>(".gnode-load");
    if (!node || !el) return;
    const bar = el.querySelector<HTMLElement>("i")!;
    node.classList.toggle("cooling", cooldown > 0);
    if (cooldown > 0) {
      bar.style.width = "100%";
      el.querySelector("span")!.textContent = `COOLDOWN ${cooldown.toFixed(1)}s`;
      return;
    }
    bar.style.width = `${Math.min(1, load) * 100}%`;
    el.classList.toggle("hot", load > 0.8 && load <= 1);
    el.classList.toggle("over", load > 1 || (tracks !== null && tracks >= FUSE_MAX_TRACKS));
    const pct = `${Math.round(load * 100)}%`;
    el.querySelector("span")!.textContent = tracks === null ? pct : `${pct} · ${tracks}/${FUSE_MAX_TRACKS} trk`;
  }

  get blockIds(): string[] {
    return this.graph.nodes.filter((n) => n.type === "block").map((n) => n.id);
  }

  // ---------------------------------------------------------------- nodes

  private mountNode(n: GraphNode): void {
    const el = document.createElement("div");
    el.className = `gnode gnode-${n.type}`;
    if (n.type === "sensor") el.classList.add(`kind-${n.sensor}`);
    if (n.type === "block") el.classList.add(`block-${BLOCKS[n.block].tag}`);
    if (n.type === "gun") el.classList.add(`gun-${n.gun}`);
    el.dataset.id = n.id;
    el.style.left = `${n.x}px`;
    el.style.top = `${n.y}px`;

    let tag: string;
    let name: string;
    let meta: string;
    let describe: string;
    if (n.type === "sensor") {
      const s = SENSORS[n.sensor];
      tag = "sensor";
      name = n.label;
      meta = `±${s.error} m${s.classifies ? " · class" : ""}${s.measuresVelocity ? " · vel" : ""}`;
      describe = `<b>${n.label}</b>: ${s.blurb}`;
    } else if (n.type === "gun") {
      const g = WEAPONS[n.gun];
      tag = n.gun === "attackDrone" ? "drone" : "gun";
      name = n.label;
      meta = `${g.range} m · ${g.reload}s · ${g.armorFactor < 1 ? "light" : "heavy"}`;
      describe = `<b>${n.label}</b>: ${g.blurb}`;
    } else {
      const b = BLOCKS[n.block];
      tag = b.tag;
      name = b.label;
      meta = `+${b.latencyMs} ms · ${b.capacity}/s`;
      describe = `<b>${b.label}</b> (+${b.latencyMs} ms, ${b.capacity} reports/s): ${b.description}`;
    }

    el.innerHTML = `
      <div class="gnode-head"><span class="gnode-tag">${tag}</span>${
        n.type === "block" ? `<button type="button" class="gnode-x" aria-label="Remove ${name}">×</button>` : ""
      }</div>
      <div class="gnode-name"></div>
      <div class="gnode-meta">${meta}</div>${
        n.type === "block" ? `<div class="gnode-load" title="Load"><i></i><span></span></div>` : ""
      }`;
    el.querySelector(".gnode-name")!.textContent = name;

    if (hasInput(n)) {
      const port = document.createElement("span");
      port.className = "port port-in";
      el.append(port);
    }
    const outs = outputsOf(n);
    outs.forEach((label, i) => {
      const port = document.createElement("span");
      port.className = "port port-out";
      port.dataset.port = String(i);
      port.style.top = `${((i + 1) / (outs.length + 1)) * 100}%`;
      if (outs.length > 1) port.innerHTML = `<i>${label}</i>`;
      port.addEventListener("pointerdown", (e) => this.startWire(e, n.id, i));
      el.append(port);
    });

    el.addEventListener("pointerenter", () => (this.info.innerHTML = describe));
    el.querySelector(".gnode-head")!.addEventListener("pointerdown", (e) => this.startMove(e as PointerEvent, n, el));
    el.querySelector(".gnode-x")?.addEventListener("click", () => this.removeNode(n.id));

    this.area.append(el);
    this.nodeEls.set(n.id, el);
  }

  private paletteButton(kind: BlockKind): HTMLElement {
    const def = BLOCKS[kind];
    const el = document.createElement("button");
    el.type = "button";
    el.className = `pblock block-${def.tag}`;
    el.innerHTML = `<span class="block-tag">${def.tag}</span> ${def.label}`;
    el.title = def.description;
    el.addEventListener("pointerenter", () => (this.info.innerHTML = `<b>${def.label}</b> (+${def.latencyMs} ms): ${def.description}`));
    el.addEventListener("click", (e) => {
      if (e.detail === 0) this.addBlock(kind, this.freeSpot()); // keyboard
    });
    el.addEventListener("pointerdown", (e) => this.startPaletteDrag(e, el, kind));
    return el;
  }

  private addBlock(kind: BlockKind, at: { x: number; y: number }): void {
    const node: GraphNode = { id: `block-${this.nextId++}`, type: "block", block: kind, ...clampNode(at) };
    this.graph.nodes.push(node);
    this.mountNode(node);
    this.changed();
  }

  private freeSpot(): { x: number; y: number } {
    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 4; col++) {
        const p = { x: 190 + col * (NODE_W + 24), y: 14 + row * (NODE_H + 12) };
        const clash = this.graph.nodes.some((n) => Math.abs(n.x - p.x) < NODE_W && Math.abs(n.y - p.y) < NODE_H);
        if (!clash) return p;
      }
    }
    return { x: 300, y: 150 };
  }

  private local(e: PointerEvent): { x: number; y: number } {
    const r = this.area.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  }

  private startPaletteDrag(e: PointerEvent, el: HTMLElement, kind: BlockKind): void {
    if (e.button !== 0) return;
    e.preventDefault();
    const sx = e.clientX;
    const sy = e.clientY;
    let ghost: HTMLElement | null = null;

    const move = (ev: PointerEvent) => {
      if (!ghost) {
        if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < DRAG_THRESHOLD_PX) return;
        ghost = el.cloneNode(true) as HTMLElement;
        ghost.classList.add("ghost");
        document.body.append(ghost);
        document.body.classList.add("is-dragging");
      }
      ghost.style.transform = `translate(${ev.clientX - 50}px, ${ev.clientY - 16}px)`;
    };
    const end = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      document.body.classList.remove("is-dragging");
      if (!ghost) {
        if (ev.type === "pointerup") this.addBlock(kind, this.freeSpot());
        return;
      }
      ghost.remove();
      if (ev.type !== "pointerup") return;
      const r = this.area.getBoundingClientRect();
      if (ev.clientX < r.left || ev.clientX > r.right || ev.clientY < r.top || ev.clientY > r.bottom) return;
      const p = this.local(ev);
      this.addBlock(kind, { x: p.x - NODE_W / 2, y: p.y - 14 });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }

  private startMove(e: PointerEvent, n: GraphNode, el: HTMLElement): void {
    if (e.button !== 0 || (e.target as HTMLElement).closest(".gnode-x")) return;
    e.preventDefault();
    const start = this.local(e);
    const ox = n.x;
    const oy = n.y;
    el.classList.add("moving");
    const move = (ev: PointerEvent) => {
      const p = this.local(ev);
      Object.assign(n, clampNode({ x: ox + p.x - start.x, y: oy + p.y - start.y }));
      el.style.left = `${n.x}px`;
      el.style.top = `${n.y}px`;
      this.renderWires();
    };
    const end = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      el.classList.remove("moving");
      this.changed();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }

  // ---------------------------------------------------------------- wires

  private startWire(e: PointerEvent, from: string, port: number): void {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const origin = this.portCenter(from, port);
    if (!origin) return;
    const temp = document.createElementNS(SVG_NS, "path");
    temp.classList.add("wire", "wire-temp");
    this.svg.append(temp);
    document.body.classList.add("is-wiring");
    let over: HTMLElement | null = null;

    const move = (ev: PointerEvent) => {
      const p = this.local(ev);
      temp.setAttribute("d", curve(origin, p));
      const target = this.nodeAt(ev.clientX, ev.clientY);
      if (target !== over) {
        over?.classList.remove("wire-target", "wire-bad");
        over = target;
        if (over) {
          const problem = wireProblem(this.graph, from, port, over.dataset.id!);
          over.classList.add(problem ? "wire-bad" : "wire-target");
        }
      }
    };
    const end = (ev: PointerEvent) => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      document.body.classList.remove("is-wiring");
      over?.classList.remove("wire-target", "wire-bad");
      temp.remove();
      if (ev.type !== "pointerup") return;
      const target = this.nodeAt(ev.clientX, ev.clientY);
      if (!target) return;
      const to = target.dataset.id!;
      if (to === from) return;
      const problem = wireProblem(this.graph, from, port, to);
      if (problem) {
        this.info.textContent = `Can't wire that: ${problem}.`;
        return;
      }
      this.graph.wires.push({ id: `wire-${this.nextId++}`, from, port, to });
      this.changed();
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }

  private nodeAt(x: number, y: number): HTMLElement | null {
    for (const el of this.nodeEls.values()) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return el;
    }
    return null;
  }

  /**
   * Port centres come from the node model, not from layout, so wires draw correctly even while the
   * screen is hidden. These offsets match the .gnode and .port rules in style.css.
   */
  private portCenter(nodeId: string, port: number | "in"): { x: number; y: number } | null {
    const n = this.graph.nodes.find((x) => x.id === nodeId);
    if (!n) return null;
    const border = 2;
    const inner = NODE_H - border * 2;
    if (port === "in") return { x: n.x + border - 1, y: n.y + border + inner / 2 };
    const outs = outputsOf(n).length;
    if (port >= outs) return null;
    return { x: n.x + NODE_W - border + 1, y: n.y + border + (inner * (port + 1)) / (outs + 1) };
  }

  private renderWires(): void {
    const live = new Set(this.graph.wires.map((w) => w.id));
    for (const [id, el] of this.wireEls) {
      if (!live.has(id)) {
        el.parentElement?.remove();
        this.wireEls.delete(id);
      }
    }
    for (const w of this.graph.wires) {
      const a = this.portCenter(w.from, w.port);
      const b = this.portCenter(w.to, "in");
      if (!a || !b) continue;
      let path = this.wireEls.get(w.id);
      if (!path) {
        const g = document.createElementNS(SVG_NS, "g");
        g.classList.add("wire-group");
        const hit = document.createElementNS(SVG_NS, "path");
        hit.classList.add("wire-hit");
        path = document.createElementNS(SVG_NS, "path");
        path.classList.add("wire");
        g.append(hit, path);
        const title = document.createElementNS(SVG_NS, "title");
        title.textContent = "Click to cut this wire";
        g.append(title);
        g.addEventListener("click", () => {
          this.graph.wires = this.graph.wires.filter((x) => x.id !== w.id);
          this.changed();
        });
        this.svg.append(g);
        this.wireEls.set(w.id, path);
      }
      const d = curve(a, b);
      path.setAttribute("d", d);
      path.previousElementSibling!.setAttribute("d", d);
    }

    // Mark sensors that feed nothing and guns that nothing feeds.
    for (const n of this.graph.nodes) {
      const el = this.nodeEls.get(n.id);
      if (!el) continue;
      const idle =
        (n.type === "sensor" && !this.graph.wires.some((w) => w.from === n.id)) ||
        (n.type === "gun" && !this.graph.wires.some((w) => w.to === n.id));
      el.classList.toggle("idle", idle);
    }
  }

  private changed(): void {
    this.renderWires();
  }
}

function curve(a: { x: number; y: number }, b: { x: number; y: number }): string {
  const dx = Math.max(40, Math.abs(b.x - a.x) / 2);
  return `M${a.x},${a.y} C${a.x + dx},${a.y} ${b.x - dx},${b.y} ${b.x},${b.y}`;
}

function clampNode(p: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.round(Math.max(4, Math.min(GRAPH_W - NODE_W - 12, p.x))),
    y: Math.round(Math.max(4, Math.min(GRAPH_H - NODE_H - 4, p.y))),
  };
}

function isFixedGun(id: string): boolean {
  return FIXED_GUNS.some((g) => g.id === id);
}

function defaultGuns(): GraphNode[] {
  return FIXED_GUNS.map((g, i) => ({
    id: g.id,
    type: "gun" as const,
    gun: g.kind,
    label: g.label,
    x: GRAPH_W - NODE_W - 16,
    y: 40 + i * 130,
  }));
}

